import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowRight,
    CheckCircle2,
    Clock3,
    FileText,
    Home,
    Inbox,
    LibraryBig,
    PlusCircle,
    RefreshCcw,
    Send,
    Warehouse,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import StatusBadge from "@/components/StatusBadge";
import api from "@/lib/api";
import { formatDate, money } from "@/lib/format";

function quoteCustomerName(quote) {
    const customer = quote?.customer_snapshot || {};
    return [customer.first_name, customer.last_name].filter(Boolean).join(" ") || "No customer selected";
}

function quoteHomeLabel(quote) {
    return quote?.home?.model_name || quote?.preferred_home_model || "Home not selected";
}

function isSameStatus(quote, status) {
    return String(quote?.status || "draft").toLowerCase() === status;
}

function totalQuoteValue(quotes) {
    return quotes.reduce((sum, quote) => sum + Number(quote?.totals?.grand_total || 0), 0);
}

function StatCard({ label, value, helper, icon: Icon, danger = false }) {
    return (
        <Card className={`rounded-xl border p-4 ${danger ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}>
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className={`text-xs uppercase tracking-wide ${danger ? "text-amber-700" : "text-slate-500"}`}>{label}</div>
                    <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
                    {helper ? <div className="mt-1 text-xs text-slate-500">{helper}</div> : null}
                </div>
                <div className={`rounded-lg p-2 ${danger ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
                    <Icon className="h-4 w-4" />
                </div>
            </div>
        </Card>
    );
}

function QuickAction({ to, title, description, icon: Icon, primary = false }) {
    return (
        <Link to={to} className="block">
            <Card className={`h-full rounded-xl border p-4 transition hover:shadow-sm ${primary ? "border-[#43a1d7] bg-[rgba(67,161,215,0.08)]" : "border-slate-200 bg-white hover:border-[#43a1d7]"}`}>
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-[#0F2A47] shadow-sm">
                        <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-semibold text-slate-900">{title}</div>
                        <div className="mt-1 text-xs leading-5 text-slate-600">{description}</div>
                    </div>
                </div>
            </Card>
        </Link>
    );
}

function QuoteList({ title, quotes, emptyText }) {
    return (
        <Card className="overflow-hidden rounded-xl border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <div>
                    <h2 className="font-semibold text-slate-900">{title}</h2>
                    <p className="text-xs text-slate-500">Fast access to quote work already in motion.</p>
                </div>
                <Link to="/quotes" className="text-xs font-medium text-[#1E6FA8] hover:underline">Open library</Link>
            </div>
            {quotes.length === 0 ? (
                <div className="p-5 text-sm text-slate-500">{emptyText}</div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {quotes.slice(0, 6).map((quote) => (
                        <Link key={quote.id} to={`/quotes/${quote.id}`} className="block px-4 py-3 hover:bg-slate-50">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="font-mono text-xs text-slate-500">{quote.quote_number || "No quote number"}</div>
                                    <div className="mt-0.5 truncate text-sm font-medium text-slate-900">{quoteCustomerName(quote)}</div>
                                    <div className="mt-0.5 truncate text-xs text-slate-500">{quoteHomeLabel(quote)} · {formatDate(quote.quote_date || quote.created_at)}</div>
                                </div>
                                <div className="shrink-0 text-right">
                                    <StatusBadge status={quote.status || "draft"} />
                                    <div className="mt-1 text-xs font-semibold text-slate-700">{money(quote.totals?.grand_total)}</div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </Card>
    );
}

function IntakeList({ intakes }) {
    return (
        <Card className="overflow-hidden rounded-xl border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <div>
                    <h2 className="font-semibold text-slate-900">Ready to Quote</h2>
                    <p className="text-xs text-slate-500">GHL imports waiting for quote creation.</p>
                </div>
                <Link to="/ready-to-quote" className="text-xs font-medium text-[#1E6FA8] hover:underline">Open queue</Link>
            </div>
            {intakes.length === 0 ? (
                <div className="p-5 text-sm text-slate-500">No imported leads are waiting right now.</div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {intakes.slice(0, 6).map((intake) => {
                        const contact = intake.contact || {};
                        const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || "Unnamed lead";
                        return (
                            <Link key={intake.id} to="/ready-to-quote" className="block px-4 py-3 hover:bg-slate-50">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="truncate text-sm font-medium text-slate-900">{name}</div>
                                        <div className="mt-0.5 truncate text-xs text-slate-500">{intake.preferences?.home_model || "Home not selected"} · {intake.assigned_user_name || intake.assigned_user_email || "Manager queue"}</div>
                                    </div>
                                    <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${intake.status === "needs_review" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
                                        {intake.status === "needs_review" ? "Needs review" : "Ready"}
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

export default function Dashboard() {
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [intakes, setIntakes] = useState([]);
    const [inventorySummary, setInventorySummary] = useState({});
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [quoteResult, intakeResult, inventoryResult] = await Promise.allSettled([
                api.get("/quotes"),
                api.get("/quote-intakes"),
                api.get("/home-inventory/summary"),
            ]);
            setQuotes(quoteResult.status === "fulfilled" ? quoteResult.value.data || [] : []);
            setIntakes(intakeResult.status === "fulfilled" ? intakeResult.value.data || [] : []);
            setInventorySummary(inventoryResult.status === "fulfilled" ? inventoryResult.value.data || {} : {});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const sortedQuotes = useMemo(() => [...quotes].sort((a, b) => new Date(b.updated_at || b.created_at || b.quote_date || 0) - new Date(a.updated_at || a.created_at || a.quote_date || 0)), [quotes]);
    const draftQuotes = useMemo(() => sortedQuotes.filter((quote) => isSameStatus(quote, "draft")), [sortedQuotes]);
    const sentQuotes = useMemo(() => sortedQuotes.filter((quote) => isSameStatus(quote, "sent")), [sortedQuotes]);
    const acceptedQuotes = useMemo(() => sortedQuotes.filter((quote) => isSameStatus(quote, "accepted")), [sortedQuotes]);
    const activeQuoteValue = useMemo(() => totalQuoteValue(sortedQuotes.filter((quote) => !["declined", "expired", "archived"].includes(String(quote.status || "draft").toLowerCase()))), [sortedQuotes]);
    const readyIntakes = useMemo(() => intakes.filter((intake) => intake.status !== "converted" && intake.status !== "dismissed"), [intakes]);

    const statusRows = [
        ["Draft", draftQuotes.length, "Quotes still being built"],
        ["Sent", sentQuotes.length, "Quotes already shared"],
        ["Accepted", acceptedQuotes.length, "Quotes marked accepted"],
        ["All Active", sortedQuotes.length, "Visible quote records"],
    ];

    return (
        <div className="space-y-6" data-testid="quote-command-center-page">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500">Quote-only portal</div>
                    <h1 className="text-3xl font-semibold text-slate-900">Quote Command Center</h1>
                    <p className="mt-1 max-w-2xl text-sm text-slate-600">
                        Manage imported GHL quote requests, manual quotes, active drafts, sent quotes, and available homes from one focused workspace.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={load} disabled={loading}>
                        <RefreshCcw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> {loading ? "Refreshing" : "Refresh"}
                    </Button>
                    <Button onClick={() => navigate("/quotes/new")} className="bg-[#0F2A47] hover:bg-[#14385E] text-white">
                        <PlusCircle className="mr-2 h-4 w-4" /> New Manual Quote
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Ready to Quote" value={readyIntakes.length} helper="Imported from GHL" icon={Inbox} danger={readyIntakes.length > 0} />
                <StatCard label="Draft Quotes" value={draftQuotes.length} helper="Need completion/review" icon={Clock3} />
                <StatCard label="Sent Quotes" value={sentQuotes.length} helper="Customer-facing quotes" icon={Send} />
                <StatCard label="Active Quote Value" value={money(activeQuoteValue)} helper="Draft + sent + accepted" icon={CheckCircle2} />
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
                <QuickAction to="/ready-to-quote" title="Start from GHL Lead" description="Use imported lead data to create a prefilled quote." icon={Inbox} primary />
                <QuickAction to="/quotes/new" title="Start Manual Quote" description="Create a quote when the customer is not coming from GHL." icon={PlusCircle} />
                <QuickAction to="/quotes" title="Quote Library" description="Search by customer, quote number, model, status, or salesperson." icon={LibraryBig} />
                <QuickAction to="/home-catalog" title="Home Catalog" description="Find the home model before building or revising a quote." icon={Home} />
            </div>

            <div className="grid gap-5 xl:grid-cols-[1fr_420px]">
                <QuoteList title="Recent Quote Activity" quotes={sortedQuotes} emptyText="No quotes have been created yet." />
                <IntakeList intakes={readyIntakes} />
            </div>

            <div className="grid gap-5 xl:grid-cols-[1fr_420px]">
                <Card className="overflow-hidden rounded-xl border-slate-200">
                    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                        <div>
                            <h2 className="font-semibold text-slate-900">Quote Status Snapshot</h2>
                            <p className="text-xs text-slate-500">High-level production view for managers.</p>
                        </div>
                        <FileText className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="divide-y divide-slate-100">
                        {statusRows.map(([label, count, helper]) => {
                            const pct = sortedQuotes.length ? Math.round((Number(count) / sortedQuotes.length) * 100) : 0;
                            return (
                                <div key={label} className="px-4 py-3">
                                    <div className="flex items-center justify-between gap-4 text-sm">
                                        <div>
                                            <div className="font-medium text-slate-900">{label}</div>
                                            <div className="text-xs text-slate-500">{helper}</div>
                                        </div>
                                        <div className="text-right font-semibold text-slate-900">{count}</div>
                                    </div>
                                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                                        <div className="h-full rounded-full bg-[#43a1d7]" style={{ width: `${Math.min(pct, 100)}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>

                <Card className="overflow-hidden rounded-xl border-slate-200">
                    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                        <div>
                            <h2 className="font-semibold text-slate-900">Inventory Support</h2>
                            <p className="text-xs text-slate-500">Available units used while quoting.</p>
                        </div>
                        <Warehouse className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-4">
                        {[
                            ["On Site", inventorySummary.on_site || 0],
                            ["Available", inventorySummary.available || 0],
                            ["Ordered", inventorySummary.ordered || 0],
                            ["In Transit", inventorySummary.in_transit || 0],
                        ].map(([label, value]) => (
                            <div key={label} className="rounded-lg border border-slate-200 p-3">
                                <div className="text-xs text-slate-500">{label}</div>
                                <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-slate-100 px-4 py-3">
                        <Button asChild variant="outline" className="w-full justify-between">
                            <Link to="/home-inventory">Open Home Inventory <ArrowRight className="h-4 w-4" /></Link>
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
    );
}
